import { useTranslation } from "react-i18next";

import { TestProps } from "shared/types";

import Typography from "features/components/Typography";
import ConfirmationModal from "features/ui/ConfirmationModal";
import TypeToConfirmModal from "features/ui/TypeToConfirmModal/TypeToConfirmModal";

interface TypeToConfirm extends TestProps {
  confirmValue: string;
  typedValue: string;
  setTypedValue: (value: string) => void;
}

// A bulk delete names the entity by an i18n key with _one/_other forms, so the count and the noun always agree.
interface CountedEntity {
  key: string;
  count: number;
}

interface Props {
  isOpen: boolean;
  onClose: (confirmed: boolean) => void;
  entityName: string | CountedEntity;
  name?: string;
  details?: string;
  loading?: boolean;
  error?: string;
  typeToConfirm?: TypeToConfirm;
}

const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  entityName,
  name,
  details,
  loading = false,
  error,
  typeToConfirm,
}: Props) => {
  const { t } = useTranslation();
  // Entity names come from Title Case labels such as "Tire Setting", but the
  // copy uses them mid-sentence.
  const entity = (
    typeof entityName === "string"
      ? entityName
      : t(entityName.key, { count: entityName.count })
  ).toLowerCase();
  const title = t("common:delete.title", { entityName: entity });
  let mainText = t("common:delete.text_unnamed", { entityName: entity });
  if (name) {
    mainText = t("common:delete.text", { entityName: entity, name });
  } else if (typeof entityName !== "string") {
    mainText = t("common:delete.text_count", {
      entityName: entity,
      count: entityName.count,
    });
  }

  const body = details ? (
    <div className="flex flex-col gap-2">
      <span>{mainText}</span>
      <span>{details}</span>
    </div>
  ) : (
    mainText
  );

  if (typeToConfirm) {
    return (
      <TypeToConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        body={
          <Typography variant="body1" component="div">
            {body}
          </Typography>
        }
        confirmValue={typeToConfirm.confirmValue}
        typedValue={typeToConfirm.typedValue}
        setTypedValue={typeToConfirm.setTypedValue}
        isLoading={loading}
        error={error}
        testId={typeToConfirm.testId}
      />
    );
  }

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      text={body}
      confirmText={t("common:delete.action")}
      confirmColor="error"
      loading={loading}
      closeDisabled={loading}
      error={error}
    />
  );
};

export default DeleteConfirmationModal;
